import { Component, Inject, NgZone } from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
} from '@angular/material/dialog';
import { randomId } from 'server/shared/helpers/random';
import { JobTypeEnum } from 'server/shared/models/jobTypeEnum';
import { Shift } from 'server/shared/models/shift';
import { CustomerService } from 'src/app/services/customer.service';
import { EditTimeDialog } from '../edit-time-dialog/edit-time-dialog.component';

export type EditShiftsDialogData = {
  availableShifts: Shift[];
  selectedShifts: Shift[];
  showMultiDayToggle: boolean;
  splitShiftsEnabled: boolean;
  disabled: boolean;
  isNewJob: boolean;
  jobType?: JobTypeEnum;
  canEditShifts: boolean;
};
@Component({
  selector: 'edit-shifts-dialog',
  templateUrl: './edit-shifts-dialog.component.html',
  styleUrls: ['./edit-shifts-dialog.component.scss'],
})
export class EditShiftsDialog {
  id = randomId().slice(0, 3);

  availableShiftsForWeekday: Shift[] = [];
  selectedShifts: Shift[] = [];
  showMultiDayToggle: boolean = false;
  splitShiftsEnabled: boolean = false;
  disabled: boolean = false;
  canEditShifts: boolean = false;

  jobTypeEnum = JobTypeEnum;
  jobType?: JobTypeEnum;

  customer$ = this.customerService.dispoCustomer$;

  constructor(
    public dialogRef: MatDialogRef<EditShiftsDialog>,
    private customerService: CustomerService,
    private ngZone: NgZone,
    private dialog: MatDialog,
    @Inject(MAT_DIALOG_DATA)
    data: EditShiftsDialogData,
  ) {
    this.canEditShifts = data.canEditShifts;
    // we don't want to edit original objects with ngModel, so we clone it.
    const clonedData: EditShiftsDialogData = JSON.parse(JSON.stringify(data));
    this.availableShiftsForWeekday = clonedData.availableShifts;
    this.jobType = clonedData.jobType;

    this.selectedShifts = this.availableShiftsForWeekday.filter(
      (sShift: Shift) =>
        clonedData.selectedShifts.some((aShift) => aShift.id === sShift.id) &&
        this.isShiftCompatible(sShift, this.jobType),
    );

    this.splitShiftsEnabled = clonedData.splitShiftsEnabled;
    this.showMultiDayToggle = clonedData.showMultiDayToggle;
    this.disabled = clonedData.disabled;

    this.updateAvailableShiftsForJobType();
  }

  private isShiftCompatible(shift: Shift, jobType?: JobTypeEnum): boolean {
    if (!jobType) return true;
    return (
      !shift.specificJobTypes ||
      shift.specificJobTypes.length === 0 ||
      shift.specificJobTypes.includes(jobType)
    );
  }

  availableShifts: Shift[] = [];

  updateAvailableShiftsForJobType() {
    this.availableShifts = this.availableShiftsForWeekday.filter((shift) =>
      this.isShiftCompatible(shift, this.jobType),
    );
  }

  onJobTypeChange(newJobType?: JobTypeEnum) {
    this.jobType = newJobType;
    this.updateAvailableShiftsForJobType();

    const compatibleSelectedShifts = this.selectedShifts.filter((shift) =>
      this.isShiftCompatible(shift, this.jobType),
    );
    this.selectedShifts = compatibleSelectedShifts.length
      ? compatibleSelectedShifts
      : [this.availableShifts[0]];
  }

  private sortShifts(shifts: Shift[]): Shift[] {
    return [...shifts].sort((a, b) => {
      // First sort by shiftEnum
      if (a.shift !== b.shift) {
        return a.shift - b.shift;
      }
      // Then sort by time (from)
      return a.from - b.from;
    });
  }

  continue(): void {
    this.dialogRef.close({
      shifts: this.sortShifts(this.selectedShifts),
      jobType: this.jobType,
    });
  }

  close(): void {
    this.dialogRef.close(false);
  }

  toggleHasMultipleShifts(checked: boolean) {
    if (!checked) {
      this.selectedShifts = [this.availableShifts[0]];
    }
    this.splitShiftsEnabled = checked;
  }

  openShiftEditDialog(event: Event, shift: Shift): void {
    event.stopPropagation();
    const dialogRef = this.dialog.open(EditTimeDialog, {
      data: shift,
    });
    dialogRef
      .afterClosed()
      .subscribe((result: { from: number; to: number }) => {
        if (result && (shift.from != result.from || shift.to != result.to)) {
          shift.from = result.from;
          shift.to = result.to;
        }
      });
  }

  compareShiftsForSelection(s1: Shift, s2: Shift): boolean {
    return s1 && s2 ? s1.id === s2.id : s1 === s2;
  }
}
